import { Box, makeStyles } from "@material-ui/core";
import { NumericFormat } from "react-number-format";
import { BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar } from "recharts";

import { GraphSectionLayout } from "./GraphSectionLayout";
import { SolarReport } from "../../../../api/Quote/SolarReport";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { fontFamily } from "../../../../app/theme";
import { ConsumptionProductionTooltip } from "./ConsumptionProductionTooltip";

export const ConsumptionProduction = ({ solarReportData }: { solarReportData: SolarReport | null }) => {
  const { isMoreThanSm } = useBreakpoint();
  const classes = useStyles();

  const month: { [key: number]: string } = {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec"
  };

  return (
    <GraphSectionLayout
      title={"Estimated Consumption vs Production"}
      description={
        "This is an estimated graph of the monthly electricity usage in your property (consumption) vs the electricity produced by your Solar PV system (production)."
      }
      leftContent={
        <Box>
          <Box
            mt={isMoreThanSm ? "45px" : "23px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>Total Annual Consumption</Box>
            <Box>
              <NumericFormat
                value={solarReportData?.consumptionAnnualKwh}
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={0}
                suffix="kWh"
              />
            </Box>
          </Box>
          <Box
            mt={"10px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            fontSize={isMoreThanSm ? "14px" : "12px"}
            lineHeight={isMoreThanSm ? 1.71 : 2}
            fontWeight={500}
            letterSpacing={"0.32px"}
            color={"#4b5462"}
          >
            <Box>Total Annual Production</Box>
            <Box color={"#f7815c"}>
              <NumericFormat
                value={solarReportData?.productionAnnualKwh}
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={0}
                suffix="kWh"
              />
            </Box>
          </Box>
        </Box>
      }
      rightContent={
        <Box>
          <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
            <Box display={"flex"} alignItems={"center"} justifyContent={"center"} fontSize={"12px"} fontWeight={500}>
              <Box
                width={"11px"}
                height={"11px"}
                mr={"4px"}
                mb={"2px"}
                borderRadius={"50%"}
                border={"solid 1px rgba(70, 60, 87, 0.5)"}
                bgcolor={"rgba(22, 3, 55, 0.6)"}
              />
              Consumption
            </Box>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              fontSize={"12px"}
              fontWeight={500}
              ml={"26px"}
            >
              <Box
                width={"11px"}
                height={"11px"}
                mr={"4px"}
                mb={"2px"}
                borderRadius={"50%"}
                border={"1px solid #eb9b81"}
                bgcolor={"rgba(251, 192, 173, 0.6)"}
              />
              Production
            </Box>
          </Box>
          <Box width={"100%"} height={"290px"} mt={"15px"}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={400}
                data={solarReportData?.consumptionByMonth.map((item, idx) => ({
                  label: month[idx + 1],
                  consumption: item,
                  production: solarReportData?.productionByMonth?.[idx]
                }))}
                className={classes.chart}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
                style={{ overflow: "inherit" }}
              >
                <CartesianGrid vertical={false} stroke="#efefef" />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  style={{ fontSize: "12px", fontFamily: fontFamily.Axiforma }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickCount={7}
                  width={isMoreThanSm ? undefined : 30}
                  tick={({ x, y, payload }: any) => {
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <NumericFormat
                          value={Number(payload.value)}
                          displayType={"text"}
                          renderText={(value) => (
                            <text
                              x={0}
                              y={0}
                              dy={0}
                              textAnchor="end"
                              fill="#666"
                              fontSize={"12px"}
                              fontWeight={500}
                              fontFamily={fontFamily.Axiforma}
                            >
                              {value}
                            </text>
                          )}
                          decimalScale={0}
                          fixedDecimalScale={true}
                          thousandSeparator={true}
                        />
                      </g>
                    );
                  }}
                />
                <Tooltip
                  content={<ConsumptionProductionTooltip />}
                  wrapperStyle={{ outline: "none" }}
                  cursor={{ fill: "none" }}
                />
                <Bar dataKey="production" stackId="a" fill="#fdd9ce" />
                <Bar dataKey="consumption" stackId="a" fill="rgba(22, 3, 55, 0.6)" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      }
    />
  );
};

const useStyles = makeStyles((theme) => ({
  chart: {
    "& .recharts-surface": {
      overflow: "inherit"
    }
  },
  labelYear: {
    "& tspan": {
      dy: "-20px"
    }
  }
}));
